import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCopierToken,
  issueCopierToken,
  revokeCopierToken,
  getCopierEaMeta,
  COPIER_EA_URL,
} from './signalDataFeeds';
import {
  getCopierSettings,
  saveCopierSizing,
  getCopierTracking,
  enableCopierTracking,
  disableCopierTracking,
  SIZING_MODE_LABELS,
  SIZING_MODE_HINTS,
  SIZING_MODES_NEEDING_VALUE,
} from './signalDataFeeds';
import { useConfirm } from './ConfirmDialog';

// Per-signal lot sizing.
//
// This exists because one global lot size genuinely cannot work. Minimum volume varies per
// symbol — 0.01 on EURUSD, 1 on most synthetics, 4 on Volatility 50, 10 on Volatility 250 (1s)
// — so a single "0.01" is below the minimum on the large majority of a Deriv symbol list and
// would quietly trade nothing. Sizing is therefore chosen per strategy, and the size-relative
// modes are resolved against each symbol's own minimum inside the terminal.
function SizingRow({ signal, onSaved }) {
  const [mode, setMode] = useState(signal.sizingMode || 'inherit');
  const [value, setValue] = useState(signal.sizingValue || '');
  const [enabled, setEnabled] = useState(signal.copyEnabled !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const needsValue = SIZING_MODES_NEEDING_VALUE.includes(mode);

  const persist = async (patch) => {
    const next = { mode, value, enabled, ...patch };
    setBusy(true);
    setError('');
    try {
      await saveCopierSizing(signal.deploymentId, {
        sizingMode: next.mode,
        sizingValue: Number(next.value) || 0,
        copyEnabled: next.enabled,
      });
      onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`signal-sizing-row${enabled ? '' : ' is-off'}`}>
      <div className="signal-sizing-name">
        <strong>{signal.name}</strong>
        <span className="muted-text">{signal.symbol} · {signal.timeframe}</span>
        {signal.copyMode === 'hold' && (
          <span className="signal-pill warn" title={
            signal.status && signal.status !== 'running'
              ? 'This signal is ' + signal.status + '. Positions already copied are left to run; nothing new is opened.'
              : 'Your subscription has lapsed. Positions already copied are left to run; nothing new is opened.'
          }>
            On hold
          </span>
        )}
      </div>

      <div className="signal-sizing-controls">
        <select
          value={mode}
          disabled={busy || !enabled}
          onChange={(event) => {
            const nextMode = event.target.value;
            setMode(nextMode);
            // Only persist immediately when the new mode needs no number — otherwise the save
            // would be rejected for a missing value the user has not typed yet.
            if (!SIZING_MODES_NEEDING_VALUE.includes(nextMode)) persist({ mode: nextMode });
          }}
        >
          {Object.entries(SIZING_MODE_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>

        {needsValue && (
          <input
            type="number"
            min="0"
            step={mode === 'min_multiple' ? '0.5' : '0.01'}
            value={value}
            placeholder={mode === 'min_multiple' ? '2' : mode === 'risk_pct' ? '1.0' : '0.01'}
            disabled={busy || !enabled}
            onChange={(event) => setValue(event.target.value)}
            onBlur={() => { if (Number(value) > 0) persist({}); }}
          />
        )}

        <label className="signal-toggle signal-sizing-enable">
          <input
            type="checkbox"
            checked={enabled}
            disabled={busy}
            onChange={(event) => { setEnabled(event.target.checked); persist({ enabled: event.target.checked }); }}
          />
          <span>Copy</span>
        </label>
      </div>

      <small className="muted-text">{SIZING_MODE_HINTS[mode]}</small>
      {error && <small className="signal-error">{error}</small>}
    </div>
  );
}

// MT5 copier setup.
//
// This panel exists because the copier's failure modes are all setup mistakes, and every one of
// them looks like "the EA is broken" from the user's side. The two that account for nearly all
// of them are called out explicitly rather than buried in a manual: the URL whitelist (without
// it every request fails with 4014 and nothing ever trades) and attaching the EA to more than
// one chart (which duplicates every position).
function CopierPanel() {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [eaMeta, setEaMeta] = useState(null);
  const [confirm, confirmDialog] = useConfirm();
  const [open, setOpen] = useState(false);
  const [signals, setSignals] = useState([]);
  const [tracking, setTracking] = useState(null);
  // Opened once, on first load, only when there is nothing set up yet — the four setup steps are
  // a call to action for a new user and reference material for everyone else. The ref stops a
  // later refresh from re-opening a panel the user has deliberately closed.
  const autoOpened = useRef(false);

  const loadSignals = useCallback(async () => {
    const payload = await getCopierSettings().catch(() => null);
    setSignals(payload?.signals || []);
  }, []);

  useEffect(() => { loadSignals(); }, [loadSignals]);

  // Whether this account is being reported to Live MT5, and what it has reported so far.
  // Failure is swallowed: tracking is an extra on top of copying, and an error here must not
  // take the copier setup panel down with it.
  const loadTracking = useCallback(async () => {
    const payload = await getCopierTracking().catch(() => null);
    setTracking(payload);
  }, []);

  useEffect(() => { loadTracking(); }, [loadTracking]);

  const toggleTracking = async () => {
    if (tracking?.enabled) {
      const confirmed = await confirm({
        title: 'Stop tracking this account?',
        body: 'Your copier stops reporting its balance and trades within a minute or so. The '
          + 'results already collected stay on your Live MT5 tab, and copying is unaffected.',
        confirmLabel: 'Stop tracking',
        tone: 'danger',
      });
      if (!confirmed) return;
    }
    setBusy(true);
    setError('');
    try {
      if (tracking?.enabled) await disableCopierTracking();
      else await enableCopierTracking();
      await loadTracking();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Whether a compiled build exists. Until one is uploaded the panel must not offer a download
  // that 404s — it falls back to the source-and-compile instructions instead.
  useEffect(() => {
    getCopierEaMeta().then(setEaMeta).catch(() => setEaMeta({ available: false }));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const payload = await getCopierToken();
      setToken(payload.token);
      if (!autoOpened.current) {
        autoOpened.current = true;
        setOpen(!payload.token);
      }
    } catch (err) {
      setError(err.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const issue = async () => {
    if (token) {
      const confirmed = await confirm({
        title: 'Replace your copier token?',
        body: 'The current token stops working immediately. Any MT5 terminal still using it '
          + 'will stop copying until you paste the new one in.',
        confirmLabel: 'Replace token',
        tone: 'danger',
      });
      if (!confirmed) return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = await issueCopierToken('MT5 copier');
      setToken(payload.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    const confirmed = await confirm({
      title: 'Turn off copying?',
      body: 'Your MT5 terminal stops receiving signals. Positions it already opened are left '
        + 'alone — close them yourself if you want out.',
      confirmLabel: 'Revoke token',
      tone: 'danger',
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      await revokeCopierToken();
      setToken(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyToken = async () => {
    if (!token?.token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError('Could not copy — select the token and copy it manually.');
    }
  };

  // What the collapsed header says, so the panel is worth glancing at without opening it.
  const summary = !token
    ? 'Not set up'
    : token.lastSeenAt
      ? `Active · last used ${new Date(token.lastSeenAt).toLocaleDateString()}`
      : 'Token ready · not used yet';

  return (
    <div className="signal-collapse signal-copier">
      <button
        type="button"
        className="signal-collapse-toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>Copy signals to MetaTrader 5</span>
        <span className="signal-collapse-summary">
          {summary}
          <em>{open ? '▲' : '▼'}</em>
        </span>
      </button>

      {!open ? null : (
      <div className="signal-collapse-body">
      <p className="muted-text">
        The copier EA mirrors every signal you own or follow into your own MT5 account. It asks
        the server what should be open and matches your terminal to it, so if MT5 is closed for
        a while it catches up on its own instead of missing trades. Included with VixPremium —
        the EA stops copying if your plan lapses.
      </p>

      {error && <p className="signal-error">{error}</p>}

      {!token ? (
        <button type="button" className="primary-cta compact-button" disabled={busy} onClick={issue}>
          {busy ? 'Working…' : 'Set up the MT5 copier'}
        </button>
      ) : (
        <>
          <div className="signal-copier-token">
            <label className="signal-field">
              <span>Your copier token</span>
              <input type="text" readOnly value={token.token} onFocus={(e) => e.target.select()} />
            </label>
            <div className="inline-actions">
              <button type="button" className="ghost-button compact-button" onClick={copyToken}>
                {copied ? 'Copied' : 'Copy'}
              </button>
              <button type="button" className="ghost-button compact-button" disabled={busy} onClick={issue}>
                Replace
              </button>
              <button type="button" className="ghost-button compact-button" disabled={busy} onClick={revoke}>
                Revoke
              </button>
            </div>
            {token.lastSeenAt ? (
              <p className="muted-text">
                Last used {new Date(token.lastSeenAt).toLocaleString()}
                {token.lastAccountLogin ? ` from account ${token.lastAccountLogin}` : ''}
                {token.lastBroker ? ` (${token.lastBroker})` : ''}.
              </p>
            ) : (
              <p className="muted-text">Not used yet — follow the steps below.</p>
            )}
          </div>

          <ol className="signal-copier-steps">
            <li>
              {eaMeta?.available ? (
                <>
                  <a href={COPIER_EA_URL} download>
                    <strong>Download the ready-to-use EA</strong>
                  </a>
                  {' '}(.ex5 — nothing to compile), then in MetaTrader open{' '}
                  <strong>File → Open Data Folder → MQL5 → Experts</strong> and put it there.
                  Restart MetaTrader afterwards.
                  <br />
                  <small className="muted-text">
                    Prefer to read or change the code first?{' '}
                    <a href="/VixSignalCopier.mq5" download>Download the source (.mq5)</a> and
                    press <strong>F7</strong> in MetaEditor to compile it yourself.
                  </small>
                </>
              ) : (
                <>
                  <a href="/VixSignalCopier.mq5" download>Download VixSignalCopier.mq5</a>, then in
                  MetaTrader open <strong>File → Open Data Folder → MQL5 → Experts</strong> and put
                  it there. Open it in MetaEditor, press <strong>F7</strong> to compile, then
                  restart MetaTrader.
                </>
              )}
            </li>
            <li>
              <strong>This is the step everyone misses.</strong> In MetaTrader go to{' '}
              <strong>Tools → Options → Expert Advisors</strong>, tick{' '}
              <em>Allow WebRequest for listed URL</em>, and add{' '}
              <code>https://vixalgotrading.com</code>. Without it the EA cannot reach the server
              and will never place a trade.
            </li>
            <li>
              Drag the EA onto <strong>one chart only</strong> — any chart, any symbol. It
              handles every symbol itself, so a second copy would double every position.
            </li>
            <li>
              Paste your token into <strong>InpToken</strong>, choose your position sizing, and
              make sure <strong>Algo Trading</strong> is enabled.
            </li>
          </ol>

          <div className="signal-copier-tracking">
            <h5>Track this account on Live MT5</h5>
            <p className="muted-text">
              The copier also reports this account's balance, trades and drawdown back to{' '}
              <strong>Dashboard → Live MT5</strong>, so you can follow it from your phone. It
              works on its own — you do not need a VixAlgoBuilder EA on the chart, which is what
              makes it the way to track an account copying an imported MQL5 signal. There is no
              token to paste; the EA is handed one automatically.
            </p>
            <label className="signal-toggle">
              <input
                type="checkbox"
                checked={tracking?.enabled !== false}
                disabled={busy || !tracking}
                onChange={toggleTracking}
              />
              <span>Report this account to Live MT5</span>
            </label>

            {!tracking ? (
              <p className="muted-text">Checking…</p>
            ) : tracking.enabled && (tracking.accounts || []).length > 0 ? (
              <ul className="signal-copier-tracked">
                {tracking.accounts.map((account) => (
                  <li key={account.id}>
                    <strong>#{account.accountLogin}</strong>
                    {account.broker ? ` · ${account.broker}` : ''}
                    {' · equity '}
                    {Number(account.equity || 0).toFixed(2)} {account.currency}
                    <span className="muted-text">
                      {account.lastSeenAt
                        ? ` · last report ${new Date(account.lastSeenAt).toLocaleString()}`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            ) : tracking.enabled ? (
              <p className="muted-text">
                Nothing reported yet. The first report arrives within a poll or two of the EA
                running — check the <strong>Live MT5</strong> tab.
              </p>
            ) : (
              <p className="muted-text">
                Off. Anything already collected is still on your Live MT5 tab.
              </p>
            )}

            <p className="muted-text">
              Reporting is read-only and self-reported by your own terminal — it is labelled that
              way everywhere it is shown. You can also switch it off in the EA itself with{' '}
              <strong>InpTrackAccount</strong>.
            </p>
          </div>

          {signals.length > 0 && (
            <div className="signal-sizing">
              <h5>Lot size per signal</h5>
              <p className="muted-text">
                Set separately for each strategy, and it overrides the EA. Minimum volume differs
                per symbol — 0.01 on EURUSD but 1, 4 or even 10 on some synthetics — so a single
                fixed lot is rejected on most symbols. <strong>Smallest this symbol allows</strong>{' '}
                works everywhere.
              </p>
              {signals.map((signal) => (
                <SizingRow key={signal.deploymentId} signal={signal} onSaved={loadSignals} />
              ))}
            </div>
          )}

          <div className="signal-copier-notes">
            <h5>Worth knowing before you leave it running</h5>
            <ul>
              <li>
                <strong>Sizing is yours, not the creator's.</strong> Fixed lot, a % of your
                balance, or mirroring the creator scaled to your account. Start small.
              </li>
              <li>
                <strong>Old signals are skipped.</strong> Anything that fired more than
                InpMaxSignalAgeMinutes ago is ignored — entering a move that already happened is
                how copiers lose money on winning signals.
              </li>
              <li>
                <strong>Different brokers name things differently.</strong> XAUUSD may be
                XAUUSD.m or GOLD on your account. The EA tries the common variants; if it can't
                find one it says so in the Experts log, and you can set it with InpSymbolMap.
              </li>
              <li>
                <strong>Your manual trades are safe.</strong> The EA only ever closes positions
                it opened itself.
              </li>
              <li>
                <strong>Losing access never closes a trade.</strong> Pausing a signal, or letting
                a subscription lapse, stops new copies — anything already open is left to run and
                still closes normally when the signal itself exits. Untick <strong>Copy</strong>
                {' '}above if you do want a signal’s open positions closed out.
              </li>
            </ul>
          </div>
        </>
      )}
      </div>
      )}

      {confirmDialog}
    </div>
  );
}

export default CopierPanel;
